// Resolves a product colour label (e.g. "Dark Green", "Heather Grey", "Navy Blue")
// to a CSS colour for swatches. Falls back to grey only when nothing matches.

const FALLBACK = "#808080";

// Fashion/catalogue names that aren't CSS keywords, or whose CSS value looks wrong on a swatch.
const COLOR_MAP = {
  "mesh black": "#1a1a1a",
  "jet black": "#0a0a0a",
  "off white": "#F5F2E8",
  "offwhite": "#F5F2E8",
  cream: "#FFFDD0",
  ecru: "#E8E0C8",
  ivory: "#FFFFF0",
  nude: "#E3BC9A",
  skin: "#E8B89A",
  camel: "#C19A6B",
  tan: "#D2B48C",
  khaki: "#C3B091",
  sand: "#C2B280",
  stone: "#B8AFA0",
  taupe: "#8B7D6B",
  mocha: "#6F4E37",
  coffee: "#6F4E37",
  chocolate: "#5C3317",
  rust: "#B7410E",
  copper: "#B87333",
  bronze: "#CD7F32",
  mustard: "#E1AD01",
  ochre: "#CC7722",
  lemon: "#FFF44F",
  peach: "#FFCBA4",
  apricot: "#FBCEB1",
  coral: "#FF7F50",
  salmon: "#FA8072",
  blush: "#DE5D83",
  rose: "#E8A0A8",
  "dusty pink": "#D4A5A5",
  "baby pink": "#F4C2C2",
  fuchsia: "#C2185B",
  magenta: "#C2185B",
  berry: "#8E3A59",
  wine: "#722F37",
  burgundy: "#800020",
  maroon: "#800000",
  cherry: "#990F02",
  mauve: "#B784A7",
  lilac: "#C8A2C8",
  lavender: "#B57EDC",
  purple: "#6A0DAD",
  violet: "#7F00FF",
  plum: "#8E4585",
  navy: "#000080",
  "navy blue": "#000080",
  "royal blue": "#4169E1",
  "sky blue": "#87CEEB",
  "baby blue": "#89CFF0",
  "powder blue": "#B0E0E6",
  denim: "#1560BD",
  indigo: "#3F3F9F",
  cobalt: "#0047AB",
  teal: "#008080",
  turquoise: "#30D5C8",
  aqua: "#00B5B8",
  mint: "#98FF98",
  sage: "#9CAF88",
  olive: "#708238",
  "olive green": "#708238",
  "bottle green": "#006A4E",
  emerald: "#50C878",
  "forest green": "#228B22",
  "army green": "#4B5320",
  lime: "#9ACD32",
  charcoal: "#36454F",
  graphite: "#474A51",
  slate: "#708090",
  ash: "#B2BEB5",
  melange: "#A9A9A9",
  "grey melange": "#A9A9A9",
  "gray melange": "#A9A9A9",
  silver: "#C0C0C0",
  gold: "#D4AF37",
  multi: "linear-gradient(135deg, #e53935, #fdd835, #43a047, #1e88e5)",
  multicolor: "linear-gradient(135deg, #e53935, #fdd835, #43a047, #1e88e5)",
  multicolour: "linear-gradient(135deg, #e53935, #fdd835, #43a047, #1e88e5)",
};

const DARKEN_WORDS = ["dark", "deep", "dusty", "muted"];
const LIGHTEN_WORDS = ["light", "pale", "pastel", "soft", "baby"];

// Words that describe fabric/pattern rather than colour; skipped when matching.
const NOISE_WORDS = new Set([
  "heather", "mesh", "solid", "melange", "jersey", "washed", "acid", "wash",
  "print", "printed", "striped", "stripe", "check", "checked", "marl", "tone",
  "colour", "color", "shade",
]);

const isValidCssColor = (value) =>
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("color", value);

// Look up a single phrase in the custom map, then as a CSS keyword ("dark green" -> "darkgreen").
const lookup = (phrase) => {
  if (!phrase) return null;
  if (COLOR_MAP[phrase]) return COLOR_MAP[phrase];
  const compact = phrase.replace(/\s+/g, "");
  if (COLOR_MAP[compact]) return COLOR_MAP[compact];
  if (isValidCssColor(compact)) return compact;
  return null;
};

const mix = (color, withColor, pct) =>
  isValidCssColor("color-mix(in srgb, red 50%, blue)")
    ? `color-mix(in srgb, ${color} ${100 - pct}%, ${withColor})`
    : color;

const toLabel = (color) => {
  if (color == null) return "";
  if (Array.isArray(color)) color = color.find((c) => c != null && String(c).trim() !== "");
  return String(color ?? "").trim();
};

export const getColorHex = (colorName) => {
  const raw = toLabel(colorName);
  if (!raw) return FALLBACK;

  // Already a CSS colour value (hex, rgb(), hsl()).
  if (/^(#|rgb|hsl)/i.test(raw) && isValidCssColor(raw)) return raw;

  const name = raw.toLowerCase().replace(/[_\-/&]+/g, " ").replace(/\s+/g, " ").trim();

  const direct = lookup(name);
  if (direct) return direct;

  const words = name.split(" ").filter(Boolean);
  const colorWords = words.filter((w) => !NOISE_WORDS.has(w));

  const cleaned = colorWords.join(" ");
  const cleanedMatch = lookup(cleaned);
  if (cleanedMatch) return cleanedMatch;

  // Try the longest trailing phrase first ("navy blue" before "blue"), then single words,
  // preferring specific catalogue names ("mustard") over generic CSS ones ("yellow").
  const modifiers = colorWords.filter((w) => DARKEN_WORDS.includes(w) || LIGHTEN_WORDS.includes(w));
  const bases = colorWords.filter((w) => !modifiers.includes(w));

  let base = null;
  for (let i = 0; i < bases.length - 1 && !base; i += 1) {
    base = lookup(bases.slice(i).join(" "));
  }
  if (!base) base = COLOR_MAP[bases.find((w) => COLOR_MAP[w])] || null;
  for (let i = bases.length - 1; i >= 0 && !base; i -= 1) {
    base = lookup(bases[i]);
  }
  if (!base) return FALLBACK;

  if (base.startsWith("linear-gradient")) return base;
  if (modifiers.some((m) => DARKEN_WORDS.includes(m))) return mix(base, "black", 35);
  if (modifiers.some((m) => LIGHTEN_WORDS.includes(m))) return mix(base, "white", 45);
  return base;
};

export default getColorHex;
